import { useEffect, useState, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Users, MagnifyingGlass, UserMinus, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { friendsApi } from "@/lib/apiClient";
import { dispatchFriendStatusChange } from "@/components/FriendButton";
import { toast } from "sonner";

const TIER_COLORS = {
    elite:    { bg: "rgba(212,175,55,0.18)", color: "#D4AF37" },
    premium:  { bg: "rgba(147,112,219,0.18)", color: "#9370DB" },
    explorer: { bg: "rgba(64,196,255,0.15)", color: "#40C4FF" },
    free:     { bg: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.5)" },
};

function tierStyle(tier) {
    return TIER_COLORS[tier?.toLowerCase()] || TIER_COLORS.free;
}

export default function FriendsListModal({ handle, isOwnProfile, onClose, onFriendRemoved }) {
    const [friends, setFriends] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [removing, setRemoving] = useState(null);

    useEffect(() => {
        if (!handle) return;
        setLoading(true);
        friendsApi
            .listFriends(handle)
            .then((r) => setFriends(r.friends || []))
            .catch(() => setFriends([]))
            .finally(() => setLoading(false));
    }, [handle]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return friends;
        return friends.filter(
            (f) =>
                f.handle?.toLowerCase().includes(q) ||
                f.name?.toLowerCase().includes(q)
        );
    }, [friends, query]);

    const handleRemove = async (friend) => {
        if (!window.confirm(`Remove @${friend.handle} from your friends?`)) return;
        setRemoving(friend.handle);
        try {
            await friendsApi.remove(friend.handle);
            setFriends((prev) => prev.filter((f) => f.handle !== friend.handle));
            dispatchFriendStatusChange(friend.handle);
            onFriendRemoved?.();
            toast.success(`@${friend.handle} removed from friends.`);
        } catch {
            toast.error("Failed to remove friend.");
        } finally {
            setRemoving(null);
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                key="friends-list-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[80] flex items-center justify-center p-4"
                style={{ background: "rgba(3,7,15,0.82)", backdropFilter: "blur(6px)" }}
                onClick={onClose}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.94, y: 24 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.94, y: 24 }}
                    transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full max-w-md rounded-3xl overflow-hidden flex flex-col"
                    style={{
                        background: "linear-gradient(160deg, rgba(18,26,42,0.98) 0%, rgba(11,19,32,0.99) 100%)",
                        border: "1px solid rgba(212,175,55,0.2)",
                        boxShadow: "0 24px 80px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.05)",
                        maxHeight: "85vh",
                    }}
                >
                    {/* Header */}
                    <div
                        className="flex items-center justify-between px-6 py-5 shrink-0"
                        style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
                    >
                        <div className="flex items-center gap-3">
                            <div
                                className="w-9 h-9 rounded-xl grid place-items-center"
                                style={{ background: "rgba(212,175,55,0.12)" }}
                            >
                                <Users size={18} color="#D4AF37" weight="fill" />
                            </div>
                            <div>
                                <div className="font-semibold text-white text-sm leading-tight">Friends</div>
                                {!loading && (
                                    <div className="text-[11px] text-white/40 mt-0.5">
                                        {friends.length} {friends.length === 1 ? "friend" : "friends"}
                                    </div>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-8 h-8 rounded-full grid place-items-center transition-colors hover:bg-white/10"
                        >
                            <X size={15} color="rgba(255,255,255,0.6)" />
                        </button>
                    </div>

                    {/* Search */}
                    {!loading && friends.length > 0 && (
                        <div className="px-5 pt-4 pb-2 shrink-0">
                            <div
                                className="flex items-center gap-2 px-3 py-2 rounded-xl"
                                style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
                            >
                                <MagnifyingGlass size={14} color="rgba(255,255,255,0.35)" />
                                <input
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search friends…"
                                    className="flex-1 bg-transparent text-white text-sm outline-none placeholder:text-white/30"
                                    autoFocus
                                />
                            </div>
                        </div>
                    )}

                    {/* Friend List */}
                    <div className="overflow-y-auto flex-1 px-5 py-3">
                        {loading ? (
                            <div className="flex flex-col gap-3 py-2">
                                {[...Array(4)].map((_, i) => (
                                    <div key={i} className="flex items-center gap-3 animate-pulse">
                                        <div className="w-11 h-11 rounded-full bg-white/10 shrink-0" />
                                        <div className="flex-1">
                                            <div className="h-3 w-28 bg-white/10 rounded mb-2" />
                                            <div className="h-2.5 w-20 bg-white/06 rounded" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="py-12 text-center">
                                <Users size={36} color="rgba(255,255,255,0.15)" className="mx-auto mb-3" />
                                <div className="text-white/40 text-sm">
                                    {query ? "No friends match your search." : "No friends yet."}
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-1">
                                {filtered.map((friend) => {
                                    const ts = tierStyle(friend.tier);
                                    return (
                                        <div
                                            key={friend.handle}
                                            className="flex items-center gap-3 p-2.5 rounded-2xl group transition-colors hover:bg-white/[0.04]"
                                        >
                                            {/* Avatar */}
                                            <Link
                                                to={`/profile/${friend.handle}`}
                                                onClick={onClose}
                                                className="shrink-0"
                                            >
                                                <img
                                                    src={
                                                        friend.avatar_url ||
                                                        `https://api.dicebear.com/7.x/notionists/svg?seed=${friend.handle}`
                                                    }
                                                    alt=""
                                                    className="w-11 h-11 rounded-full object-cover"
                                                    style={{ border: "1.5px solid rgba(212,175,55,0.3)" }}
                                                />
                                            </Link>

                                            {/* Info */}
                                            <div className="flex-1 min-w-0">
                                                <Link
                                                    to={`/profile/${friend.handle}`}
                                                    onClick={onClose}
                                                    className="flex items-center gap-2 group/link"
                                                >
                                                    <span className="text-white text-sm font-medium truncate group-hover/link:text-[#D4AF37] transition-colors">
                                                        {friend.name || `@${friend.handle}`}
                                                    </span>
                                                    {friend.tier && friend.tier !== "free" && (
                                                        <span
                                                            className="shrink-0 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold"
                                                            style={{ background: ts.bg, color: ts.color }}
                                                        >
                                                            {friend.tier}
                                                        </span>
                                                    )}
                                                </Link>
                                                <div className="text-white/45 text-xs truncate mt-0.5">
                                                    @{friend.handle}
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <div className="flex items-center gap-1 shrink-0">
                                                {isOwnProfile && (
                                                    <button
                                                        onClick={() => handleRemove(friend)}
                                                        disabled={removing === friend.handle}
                                                        title="Remove friend"
                                                        className="w-8 h-8 rounded-full grid place-items-center transition-colors opacity-0 group-hover:opacity-100 hover:bg-red-500/20"
                                                    >
                                                        <UserMinus
                                                            size={14}
                                                            color={removing === friend.handle ? "rgba(255,255,255,0.3)" : "#ef4444"}
                                                        />
                                                    </button>
                                                )}
                                                <Link
                                                    to={`/profile/${friend.handle}`}
                                                    onClick={onClose}
                                                    className="w-8 h-8 rounded-full grid place-items-center transition-colors opacity-0 group-hover:opacity-100 hover:bg-white/10"
                                                >
                                                    <ArrowRight size={14} color="rgba(255,255,255,0.6)" />
                                                </Link>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer count when filtered */}
                    {!loading && query && filtered.length > 0 && (
                        <div
                            className="px-6 py-3 text-center text-[11px] text-white/30 shrink-0"
                            style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
                        >
                            Showing {filtered.length} of {friends.length} friends
                        </div>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
